/* Founders (editorial): automatic lean + reveal-on-scroll. Vanilla, no dependencies.
   Works from the cards' real order, never from DOM position (blocks may be wrapped). */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const MAG = [2.4, 1.9, 2.2]; // must match _fe-card.liquid

  // Liquid already resolves "Automatic" settings from block order; this re-applies them from
  // the real DOM order so an editor reorder (or an unexpected block type name) can't break it.
  function arrange(root) {
    const grid = root.querySelector('[data-fe-grid]');
    const itemOf = (card) => { let x = card; while (x.parentElement && x.parentElement !== grid) x = x.parentElement; return x; };
    const isCard = (el) => !!el && (el.matches('[data-fe-card]') || !!el.querySelector('[data-fe-card]'));
    const sib = (el, dir) => { do { el = el[dir]; } while (el && el.matches('style, script')); return el; };
    root.querySelectorAll('[data-fe-card]').forEach((card, i) => {
      const item = itemOf(card);
      card.classList.toggle('fe-card--tuck', isCard(sib(item, 'previousElementSibling')));
      card.classList.toggle('fe-card--reserve', isCard(sib(item, 'nextElementSibling')));
      /* Each card overlaps the one before it (Loránd lowest, Kata on top). Set on the block
         wrapper: it runs the entrance (transform/opacity), which makes it its own stacking
         layer while entering, so an order set only on the card inside applied only after the
         entrance, and finished cards popped above ones still coming in. */
      const layer = card.parentElement && card.parentElement.classList.contains('shopify-block') ? card.parentElement : card;
      layer.style.setProperty('--fe-z', String(10 + i));
      const even = i % 2 === 1;
      if (card.dataset.autoPos) {
        card.classList.remove('fe-card--pos-left', 'fe-card--pos-center', 'fe-card--pos-right');
        card.classList.add(even ? 'fe-card--pos-right' : 'fe-card--pos-left');
      }
      if (card.dataset.autoPhoto) {
        const right = card.classList.contains('fe-card--pos-right');
        card.classList.remove('fe-card--photo-left', 'fe-card--photo-right');
        card.classList.add(right ? 'fe-card--photo-right' : 'fe-card--photo-left');
      }
      if (card.dataset.autoLean) card.style.setProperty('--fe-tilt', (even ? '' : '-') + MAG[i % 3] + 'deg');
    });
  }


  /* Safety net for titles on narrow screens: words are never split (CSS), so if the longest
     word is still wider than the panel, shrink that title just enough. Re-measured on resize. */
  function fitTitles(scope, selector) {
    scope.querySelectorAll(selector).forEach(function (el) {
      el.style.removeProperty('font-size');
      var avail = el.clientWidth;
      if (!avail) return;
      var size = parseFloat(getComputedStyle(el).fontSize);
      for (var i = 0; i < 12 && el.scrollWidth > avail + 1 && size > 14; i++) {
        size = Math.max(14, size * Math.max(0.8, (avail / el.scrollWidth) * 0.98));
        el.style.setProperty('font-size', size + 'px', 'important');
      }
    });
  }
  function watchTitles(scope, selector) {
    var run = function () { fitTitles(scope, selector); };
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    var t; window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(run, 150); });
  }

  function imagesReady(el, cap) {
    const imgs = [...el.querySelectorAll('.fe-card__icon img, .fe-card__img')].filter((i) => !(i.complete && i.naturalWidth));
    imgs.forEach((i) => { i.loading = 'eager'; }); // it is on screen now: don't let lazy-loading wait
    /* Also wait for the web fonts: they change the card's height, and with the card's lean that
       moves the corner icon, so the phone inset (makeRoom, re-run on fonts.ready before this)
       changed after the card was showing and the card and icon jumped sideways. */
    const fonts = document.fonts && document.fonts.ready ? document.fonts.ready.catch(() => {}) : Promise.resolve();
    const loaded = Promise.all([fonts, ...imgs.map((i) => (i.decode ? i.decode() : Promise.resolve()).catch(() => {}))]);
    return Promise.race([loaded, new Promise((r) => setTimeout(r, cap))]);
  }

  const inPageTransition = () => {
    try { return document.documentElement.matches(':active-view-transition'); } catch (e) { return false; }
  };
  function afterPageTransition(fn) {
    const t0 = performance.now();
    const tick = () => (inPageTransition() && performance.now() - t0 < 2500 ? requestAnimationFrame(tick) : fn());
    requestAnimationFrame(tick); // the transition only exists from the first drawn frame on
  }

  function reveal(root) {
    const grid = root.querySelector('[data-fe-grid]');
    if (!grid || root.dataset.motion === 'none' || reduce.matches || !('IntersectionObserver' in window)) {
      root.classList.remove('fe--pre');
      return;
    }
    const items = [...grid.children].filter((el) =>
      !el.matches('style, script, [data-fe-static]') && !el.querySelector('[data-fe-static]'));
    items.forEach((el) => el.classList.add('fe-reveal', 'fe-pending'));
    root.classList.add('fe--animate');
    const io = new IntersectionObserver((entries) => {
      let n = 0;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const el = e.target, delay = n++ * 160;
        io.unobserve(el);
        // Wait for this item's icon and photo (at most 1.5 s): a card already on screen at load
        // otherwise plays its fade and icon bounce before they have downloaded, and the icon then
        // pops in late at full size.
        imagesReady(el, 1500).then(() => {
          el.style.setProperty('--fe-delay', delay + 'ms');
          el.classList.remove('fe-pending');
          // Drop the stagger afterwards so hover responds instantly.
          setTimeout(() => el.style.removeProperty('--fe-delay'), delay + 1600);
        });
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    // Savor slides the page in on navigation (a view transition). An entrance that plays during
    // that slide is hidden by it: why the first, already-visible card seemed to skip its fade and
    // icon bounce. Start observing once the first frame is drawn and no transition is running.
    afterPageTransition(() => items.forEach((el) => io.observe(el)));
  }

  /* Phones: a corner icon set to reach further out ("Icon distance from the corner (phone)")
     than the screen margin allows was pushed back to the screen edge, so every value beyond
     about -16 looked the same. Instead the card moves inward (and gets narrower) by exactly
     what the icon needs on its side, so the setting works as named and the icon stays on
     screen. Measured at the card's normal place; nothing changes where the icon fits. */
  function makeRoom(root) {
    const phone = window.innerWidth < 750, W = document.documentElement.clientWidth, EDGE = 4;
    root.querySelectorAll('.fe-card').forEach((card) => {
      card.style.removeProperty('--fe-room'); card.removeAttribute('data-fe-room');
      const icon = card.querySelector('.fe-card__icon');
      if (!phone || !icon) return;
      const left = /--(top|bottom)-left\b/.test(icon.className), right = /--(top|bottom)-right\b/.test(icon.className);
      if (!left && !right) return;
      /* From layout, not the box on screen: the icon may be mid-entrance (growing in), which made
         it measure far too small. offsetLeft ignores transforms (entrance, nudge); the icon's own
         rotation widens it by size/2 * (|cos| + |sin| - 1) on each side. */
      const pos = (el) => { let x = 0, y = 0; for (let n = el; n; n = n.offsetParent) { x += n.offsetLeft; y += n.offsetTop; } return [x - window.scrollX, y]; };
      const paper = card.querySelector('.fe-card__paper') || card;
      const [ix, iy] = pos(icon), [px, py] = pos(paper), iw = icon.offsetWidth, ih = icon.offsetHeight;
      const rad = (v) => (parseFloat(v) || 0) * Math.PI / 180;
      const th = rad(getComputedStyle(icon).getPropertyValue('--hb-icon-rot'));          // the icon's own turn, around its centre
      const pcs = getComputedStyle(paper);
      const tilt = rad(pcs.getPropertyValue('--fe-tilt')) * (parseFloat(pcs.getPropertyValue('--fe-tilt-k')) || .5); // the card's final lean, around its centre
      const icx = ix + iw / 2, icy = iy + ih / 2, pcx = px + paper.offsetWidth / 2, pcy = py + paper.offsetHeight / 2;
      let minX = Infinity, maxX = -Infinity;
      for (const [dx, dy] of [[-iw / 2, -ih / 2], [iw / 2, -ih / 2], [iw / 2, ih / 2], [-iw / 2, ih / 2]]) {
        const qx = icx + dx * Math.cos(th) - dy * Math.sin(th), qy = icy + dx * Math.sin(th) + dy * Math.cos(th);
        const rx = pcx + (qx - pcx) * Math.cos(tilt) - (qy - pcy) * Math.sin(tilt);
        minX = Math.min(minX, rx); maxX = Math.max(maxX, rx);
      }
      const edge = left ? minX : W - maxX; // where the icon's outer edge lands once everything has settled
      const need = Math.ceil(EDGE - edge);
      if (need > 0) { card.style.setProperty('--fe-room', need + 'px'); card.setAttribute('data-fe-room', left ? 'left' : 'right'); }
    });
  }
  function watchRoom(root) {
    const run = () => makeRoom(root);
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    let t; window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(run, 150); });
  }

  function init(root) {
    if (!root || 'feReady' in root.dataset) return;
    root.dataset.feReady = '';
    arrange(root);
    watchRoom(root);
    watchTitles(root, '.fe-title :is(.text-block, h1, h2, h3, h4, h5, h6, p):not(:has(:is(h1, h2, h3, h4, h5, h6, p)))');
    reveal(root);
  }

  const all = (scope = document) => scope.querySelectorAll('[data-fe]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => all()) : all();
  document.addEventListener('shopify:section:load', (e) => all(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    e.target.closest?.('.fe-pending')?.classList.remove('fe-pending');
    const root = e.target.closest?.('[data-fe]');
    if (root) arrange(root); // editor drags can reorder cards without re-running init
  });
})();
